import React, { useState, useEffect } from "react";
import { audioService } from "../../../services/audio.service";
import { Button, Tooltip } from "@mui/material";
import MicLevelIcon from "./MicLevelIcon";
import { MutedMicIcon } from "../../../assets/icons";
import { cn } from "../../../common/utils/cn";
import { useAudioMute } from "../../../hooks/useAudioMute";
import { toast } from "../../shared/ToastNotification";
import { usePause } from "../../../context/pause";
import { WINDOW_EVENTS } from "../../../common/constants/app.constants";

interface AudioControlsProps {
  isConnected: boolean;
  avatarReady: boolean;
  isDisconnecting?: boolean;
  isSpeaking?: boolean;
}

const AudioControls: React.FC<AudioControlsProps> = ({
  isConnected,
  avatarReady,
  isDisconnecting = false,
  isSpeaking = false,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const { isMuted, toggleMute, canToggle } = useAudioMute({
    isConnected,
    avatarReady,
    isDisconnecting,
    isSpeaking,
  });
  const { isPaused } = usePause();

  // Monitor audio level when recording
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isRecording) {
      intervalId = setInterval(() => {
        const level = audioService.getAudioLevel();
        setAudioLevel(level);
      }, 100);
    }

    // Listen for recording status changes
    const handleRecordingStatusChanged = (event: any) => {
      setIsRecording(event.detail.isRecording);
    };

    window.addEventListener(
      WINDOW_EVENTS.RECORDING_STATUS_CHANGED,
      handleRecordingStatusChanged
    );

    return () => {
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener(
        WINDOW_EVENTS.RECORDING_STATUS_CHANGED,
        handleRecordingStatusChanged
      );
    };
  }, [isRecording]);

  // Keyboard shortcut for mute/unmute
  useEffect(() => {
    const handleGlobalSpaceDownToggleMic = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditable =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          (target as HTMLElement).isContentEditable ||
          target.getAttribute("role") === "textbox");

      if (isEditable) return;

      if (e.code !== "Space" || e.repeat) return;

      if (!canToggle) {
        toast({
          title: "Cannot toggle microphone",
          variant: "error",
          description:
            "Cannot toggle microphone because avatar is paused or not connected",
        });
        return;
      }

      toggleMute();
    };

    window.addEventListener("keydown", handleGlobalSpaceDownToggleMic);
    return () =>
      window.removeEventListener("keydown", handleGlobalSpaceDownToggleMic);
  }, [toggleMute, canToggle]);

  return (
    <div>
      <div className="flex items-center gap-4">
        <Tooltip
          title={"Avatar is paused"}
          placement="top"
          arrow
          disableHoverListener={!isPaused || !avatarReady}
        >
          <span>
            <Button
              variant="contained"
              className={cn(
                "!w-[56px] !h-[56px] !rounded-full !bg-primary-grey-200 !border-[1px] !border-primary-white-10 !border-solid !shadow-none !min-w-0",
                !avatarReady && "[&_path]:!fill-primary-white-10"
              )}
              onClick={toggleMute}
              disabled={!canToggle}
            >
              {isMuted ? (
                <MutedMicIcon />
              ) : (
                <MicLevelIcon level={isConnected ? audioLevel : 0} size={24} />
              )}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default AudioControls;
